import { TIME_UNITS } from '@federa/const/time-units';

/** Max retry delay (ms) for the WebSocket coordinator backoff. */
const WEBSOCKET_TIMING_MAX_RETRY_DELAY = 30000;

export const WS_BEARER_PROTOCOL = 'bearer';

/** RFC 6455 close codes used by the fleet, grouped per the `HTTP_STATUS` pattern (QUAL-CONST-GROUP-001). */
export const WS_CLOSE = {
	NORMAL: 1000,
	GOING_AWAY: 1001,
	POLICY_VIOLATION: 1008,
	MESSAGE_TOO_BIG: 1009,
	INTERNAL_ERROR: 1011,
} as const;

/**
 * Session-map key for a socket that upgraded with NO proven identity.
 *
 * The PropertyDO admits credential-less upgrades on purpose -- listing data is public and the DO is keyed by a PROPERTY
 * id -- but the broadcast fan-out reads `sessions`, which is keyed by user id, so an anonymous socket was connected and
 * unreachable: no auction-extension frames, no heartbeat. This key gives those sockets a bucket to live in.
 *
 * It is NOT an identity and must never be read as one. It never reaches an attachment, so `socketActorId` and the
 * G4/actor gates still see `null` for these sockets and deny every actor-scoped operation exactly as before. The double
 * underscores keep it outside the minted id space (`GUEST_ID_PREFIX` guests and Firebase subjects alike), so it cannot
 * collide with a real key in the same map.
 */
export const ANONYMOUS_WS_SESSION_KEY = '__anonymous__';

/** Maximum allowed WebSocket message size in bytes (64 KB) */
export const WS_MAX_MESSAGE_BYTES = 65536;

/** The connect-snapshot frame type `BaseStateDO` stamps when a subclass does not override `getWebSocketMessageType`.
 *  Every DO on that base pushes one frame with this `type` the moment a socket is accepted, and the browser hooks
 *  suppress exactly the FIRST one as "not a change" -- so this word is a TWO-SIDED CONTRACT, not a base-class detail.
 *  Spelled independently at the base class and at the consumer, changing one alone kills the first-frame suppression
 *  silently, and every page load then fires a redundant refetch. That regression is already
 *  MEASURED and recorded in `tools/durable-objects/base-state-do.ts`'s own docstring, which is why this is a derive
 *  rather than a coincidence. */
export const BASE_STATE_WS_MESSAGE_TYPE = 'state' as const;

/** WS message types sent by PropertyDO to UserDO over inter-DO WebSocket */
export const PROPERTY_WS_MESSAGE_TYPES = {
	UPDATE: 'propertyUpdate',
} as const;

export const WS_DATA_TYPES = {
	USER_DATA: 'userData',
	CURRENT_BALANCE: 'Current_Balance',
	PROPERTIES: 'properties',
	PROPERTY_DETAILS: 'propertyDetails',
	SINGLE_PROPERTY_DATA: 'singlePropertyData',
	SEARCH_RESULTS: 'searchResults',
	BIDS: 'bids',
	OFFERS: 'offers',
	AUCTION: 'auction',
	AUCTION_EXTENDED: 'auctionExtended',
	ACTIVITIES: 'activities',
	TRANSACTIONS: 'transactions',
	PROPERTY_FACTS: 'propertyFacts',
} as const;

/* NO FAVORITE-PROPERTY MEMBERS: those values are ACTIVITY TYPES (`ACTIVITY_TYPES_UI`) -- that vocabulary owns the event
 * catalog, the `types/api-activity.ts` union and every other consumer -- and a duplicate spelling here forces a
 * cross-vocabulary comparison of a value read off `payload.activity_type` against a `WS_DATA_TYPES` member.
 * `ACTIVITY_TYPES` is the sole owner; the UserDO `getData` arm keyed on these values reads it directly, which also
 * states plainly that the wire value IS the activity type. */

/** SSOT for auction operation verbs on the WebSocket data-operation protocol. Producer and handler registry must
 * reference these constants -- never inline literals -- so drift becomes a TypeScript error rather than a silent
 * no-op routed through the executor's default branch.
 */
export const AUCTION_OPERATIONS = {
	START: 'start',
	MARK_WON: 'markWon',
	MARK_LOST: 'markLost',
	CANCELED: 'canceled',
	HANDLE_BUY_SIDE_OFFER: 'handleBuySideOffer',
	HANDLE_LOST_COMPETING: 'handleLostCompeting',
} as const;

/** SSOT for bid operation verbs on the WebSocket data-operation protocol. Producer and the PropertyDO bids dispatch
 * table must reference these constants -- never inline literals -- so drift becomes a TypeScript error rather than a
 * silent no-op routed through the executor's default branch.
 */
export const BIDS_OPERATIONS = {
	CREATE: 'create',
	DELETE_ONE: 'deleteOne',
	DELETE_ALL: 'delete',
} as const;

/** SSOT for offer operation verbs on the WebSocket data-operation protocol. Producer and the offers handler registry
 * must reference these constants -- never inline literals -- so drift becomes a TypeScript error rather than a silent
 * no-op routed through the executor's default branch.
 */
export const OFFER_OPERATIONS = {
	CANCEL: 'cancel',
} as const;

const WEBSOCKET_MAX_RETRIES = 10;

export const WAIT_FOR_PRIORITY_DELAY_MS = 50;

/**
 * How long a ChatRoom trusts a conversation-access verdict it proved itself. ONE window for both places it applies:
 * the live-socket message path re-checks membership after it (`ws-message-processing`), and the UPGRADE path reuses
 * a verdict for the SAME credential and subject within it instead of re-running the token verify plus two D1
 * lookups (~0.7-1.1s on dev, measured as the whole "Connecting..." the assistant showed on every open). A removed
 * participant is therefore shut out of a NEW socket no later than they are shut out of an existing one.
 */
export const CHAT_ACCESS_VERDICT_TTL_MS = 60_000;

/**
 * Lifetime of the signed ROOM TICKET `room/create` hands a verified, named caller for the room it just proved they
 * own (same HMAC and claim shape as the guest session token: `gid` = the caller's id, `cid` = the room). The socket
 * upgrade presenting it verifies one signature instead of the Firebase JWT plus two D1 lookups (~1.1s on dev,
 * measured 2026-09-03 as the whole "Connecting..." of every assistant open). Fifteen minutes: every open re-mints
 * through `room/create`, so the ticket only needs to cover the opens and reconnects of one sitting, and a stolen
 * ticket buys the upgrade to ONE room for less time than the Firebase token it replaces would have.
 */
export const CHAT_ROOM_TICKET_TTL_SEC = 15 * TIME_UNITS.MINUTE_S;

export const CONNECT_RETRY_DELAY_MS = 500;

export const EXPLORE_CONNECTION_PRIORITY = 1;

/** Maximum number of results the explore feed will return per query. */
export const EXPLORE_MAX_RESULTS_LIMIT = 100;

/** Retry config for the WebSocket connection coordinator, jitter included -- ALL FIVE knobs of the one backoff policy.
 *  `baseRetryDelayMs` was the odd one out: it lived as a bare `1000` inside the backoff call in
 *  `tools/websocket/connection-coordinator.ts` while its four siblings were read from here, so retuning the policy
 *  meant editing two files and a reader of this record saw a policy that was only four-fifths stated. */
export const WEBSOCKET_COORDINATOR_RETRY_CONFIG = {
	maxRetries: WEBSOCKET_MAX_RETRIES,
	/** First-attempt delay the exponential curve grows from, before the max clamp and the jitter spread. */
	baseRetryDelayMs: 1000,
	minRetryDelayMs: 500,
	maxRetryDelayMs: WEBSOCKET_TIMING_MAX_RETRY_DELAY,
	jitterFactor: 0.2,
} as const;

export const EVENT_SOURCE_NEXTJS = 'nextjs';

export const TELEMETRY_BATCH_SIZE = 10;

export const TELEMETRY_MAX_QUEUE_SIZE = 100;

export const TELEMETRY_ENDPOINT = '/api/t';
